import axios from 'axios';
import { checkCreatorUpload, CREATOR_CHUNK_BYTES, uploadCreatorChunks } from './creatorUpload';

export const CREATOR_API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/public/creator`;
export const CREATOR_SESSION_KEY = 'steelmora-creator-v1';
export const creatorHeaders = session => ({ 'X-Creator-Token': session.access_token });
const projectUrl = session => `${CREATOR_API}/projects/${encodeURIComponent(session.project_id)}`;
const options = (session, signal) => ({ headers: creatorHeaders(session), ...(signal ? { signal } : {}) });
export const creatorApi = {
  capabilities: signal => axios.get(`${CREATOR_API}/capabilities`, { signal }).then(result => result.data),
  create: () => axios.post(`${CREATOR_API}/projects`, {}).then(result => result.data),
  load: (session, signal) => axios.get(projectUrl(session), options(session, signal)).then(result => result.data),
  activity: session => axios.post(`${projectUrl(session)}/activity`, {}, options(session)).then(result => result.data),
  save: (session, project) => axios.put(projectUrl(session), project, options(session)).then(result => result.data),
  upload: (session, file, onUploadProgress, uploadOptions = {}) => {
    checkCreatorUpload(file, uploadOptions.capabilities);
    if (file.size > CREATOR_CHUNK_BYTES && uploadOptions.capabilities?.uploads?.protocol === 'chunked-v1') {
      return uploadCreatorChunks(projectUrl(session), creatorHeaders(session), file, onUploadProgress, uploadOptions);
    }
    const body = new FormData();
    body.append('file', file);
    uploadOptions.onStage?.('uploading');
    return axios.post(`${projectUrl(session)}/assets`, body, { ...options(session, uploadOptions.signal), timeout: 0,
      onUploadProgress: event => { onUploadProgress?.(event); if (event.total && event.loaded >= event.total) uploadOptions.onStage?.('processing'); },
    }).then(result => result.data);
  },
  sync: (session, camera_asset_id, master_asset_id, signal) => axios.post(`${projectUrl(session)}/sync`, { camera_asset_id, master_asset_id }, options(session, signal)).then(result => result.data),
  export: (session, settings) => axios.post(`${projectUrl(session)}/exports`, settings, options(session)).then(result => result.data),
  exportStatus: (session, signal) => axios.get(`${projectUrl(session)}/export`, options(session, signal)).then(result => result.data),
  cancelExport: session => axios.delete(`${projectUrl(session)}/export`, options(session)).then(result => result.data),
  renameAsset: (session, id, payload) => axios.patch(`${projectUrl(session)}/assets/${encodeURIComponent(id)}`, payload, options(session)).then(result => result.data),
  deleteAsset: (session, id, payload) => axios.delete(`${projectUrl(session)}/assets/${encodeURIComponent(id)}`, { ...options(session), data: payload }).then(result => result.data),
  replaceAsset: (session, id, payload) => axios.post(`${projectUrl(session)}/assets/${encodeURIComponent(id)}/replace`, payload, options(session)).then(result => result.data),
};
export function readCreatorSession() {
  try {
    const value = JSON.parse(sessionStorage.getItem(CREATOR_SESSION_KEY));
    return value && /^[a-zA-Z0-9_-]{1,128}$/.test(value.project_id) && typeof value.access_token === 'string' && value.access_token.length >= 32
      ? { project_id: value.project_id, access_token: value.access_token } : null;
  } catch { return null; }
}
export function storeCreatorSession(session) {
  try {
    sessionStorage.setItem(CREATOR_SESSION_KEY, JSON.stringify({ project_id: session.project_id, access_token: session.access_token }));
    return true;
  } catch { return false; }
}
export function clearCreatorSession() {
  try { sessionStorage.removeItem(CREATOR_SESSION_KEY); } catch { /* Storage can be disabled. */ }
}
export function creatorMediaUrl(path, session, download = false) {
  if (!path || !session) return '';
  try {
    const backend = new URL(process.env.REACT_APP_BACKEND_URL || window.location.origin, window.location.origin);
    const url = new URL(path, backend);
    const prefix = `/api/public/creator/projects/${encodeURIComponent(session.project_id)}/`;
    if (url.origin !== backend.origin || !url.pathname.startsWith(prefix)) return '';
    url.searchParams.set('access_token', session.access_token);
    if (download) url.searchParams.set('download', 'true');
    return url.href;
  } catch { return ''; }
}
export function creatorError(error, fallback = 'No se pudo completar la operacion. Reintenta.') {
  const detail = error?.response?.data?.detail;
  return (typeof detail === 'string' ? detail : detail?.message) || fallback;
}
export function creatorExportFormats(capabilities) {
  if (!capabilities) return [];
  const values = capabilities.export_formats || capabilities.formats || [];
  const formats = Array.isArray(values) ? values : [];
  return formats.map(value => typeof value === 'string' ? { id: value, name: value.toUpperCase(), can_encode: true } : value)
    .filter(value => value.can_encode !== false && value.available !== false);
}
